import React, { useState, useEffect } from 'react';
import {
    Row, Col, Form, Label, Input, Card, CardBody,
    Container, Button, FormFeedback, InputGroup
} from "reactstrap";
import { toast } from "react-toastify";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import "flatpickr/dist/themes/material_blue.css";
import Flatpickr from "react-flatpickr";

const PromoCodesCreate = () => {
    const [title, setTitle] = useState('');
    const [code, setCode] = useState('');
    const [value, setValue] = useState('');
    const [scope, setScope] = useState('');
    const [type, setType] = useState('');
    const [serviceType, setServiceType] = useState('');
    const [category, setCategory] = useState('');
    const [trips, setTrips] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const navigate = useNavigate();

    const loadCustomerOptions = async (inputValue) => {
        try {
            const token = localStorage.getItem("authToken");
            const query = inputValue ? `&search=${encodeURIComponent(inputValue)}` : "";
            const response = await fetch(
                `${process.env.REACT_APP_WEB_APP_URL}/api/web/customers?limit=50${query}`,
                {
                    method: "GET",
                    headers: {
                        "Accept": "application/json",
                        "Authorization": `Bearer ${token}`,
                    }
                }
            );
            const data = await response.json();
            if (response.status === 200) {
                return (data.data || []).map(c => ({
                    value: c.user_id,
                    label: `${c.user_name}${c.email ? ` (${c.email})` : ""}${c.mobno ? ` - ${c.phone_code ?? ""}${c.mobno}` : ""}`
                }));
            }
            return [];
        } catch (err) {
            console.error(err);
            return [];
        }
    };

    const generateCode = () => {
        const randomCode = "PROMO-" + Math.random().toString(36).substring(2, 10).toUpperCase();
        setCode(randomCode);
    };

    const validateFields = () => {
        const e = {};
        if (!title.trim()) e.title = "Promo Title is required";
        if (!code) e.code = "Code is required";
        if (!value) e.value = "Value is required";

        if (type === "percent" && Number(value) > 100) {
            e.value = "Percent value cannot be greater than 100";
        }

        if (!scope) e.scope = "Scope is required";
        if (scope === "individual" && !selectedCustomer) e.customer = "Please select a customer";
        if (!type) e.type = "Type is required";
        if (!serviceType) e.serviceType = "Service Type is required";
        if (!category) e.category = "Category is required";
        if (category === "dates") {
            if (!startDate) e.startDate = "Start date is required";
            if (!endDate) e.endDate = "End date is required";
        }
        if (category === "trip" && !trips) {
            e.trips = "Number of trips is required";
        }

        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitted(true);

        if (!validateFields()) return;

        setLoading(true);

        const formData = new FormData();
        formData.append('name', title);
        formData.append('code', code);
        formData.append('value', value);
        formData.append('promocode_scope', scope);
        formData.append('type', type);
        formData.append('service_type', serviceType);
        formData.append('promocode_category', category);
        formData.append('start_date', startDate ? startDate.toISOString().split("T")[0] : "");
        formData.append('end_date', endDate ? endDate.toISOString().split("T")[0] : "");

        if (scope === "individual" && selectedCustomer) {
            formData.append("user_id", selectedCustomer.value);
        }

        if (category === "trip") {
            formData.append('trip_count', trips);
        }

        try {
            const token = localStorage.getItem("authToken");
            const response = await fetch(`${process.env.REACT_APP_WEB_APP_URL}/api/web/promo-codes/create`, {
                method: "POST",
                headers: {
                    "Accept": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: formData,
            });

            const data = await response.json();

            if (response.status === 200) {
                toast.success('Promo Code created successfully', { autoClose: 3000 });
                setLoading(false);
                navigate(`/promo-codes`);
            } else {
                setLoading(false);
                toast.error(data?.message || 'Error creating Promo Code', { autoClose: 3000 });
            }
        } catch (err) {
            setLoading(false);
            console.log(err);
            toast.error("Failed to create Promo Code", { autoClose: 3000 });
        }
    };



    document.title = "Add Promo Code | Moeen";

    return (
        <div className="page-content">
            <Container fluid={true}>
                <Breadcrumbs maintitle="Home" title="Promo Codes" breadcrumbItem="Add Promo Code" />

                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardBody>
                                <Form onSubmit={handleSubmit}>
                                    <Row>
                                        <Col md="6">
                                            {/* Title */}
                                            <div className="mb-3">
                                                <Label>Name</Label>
                                                <Input
                                                    type="text"
                                                    placeholder="Enter promo title"
                                                    value={title}
                                                    onChange={e => setTitle(e.target.value)}
                                                    invalid={isSubmitted && !!errors.title}
                                                />
                                                {isSubmitted && errors.title && (
                                                    <FormFeedback>{errors.title}</FormFeedback>
                                                )}
                                            </div>

                                            {/* Code with Generate button */}
                                            <div className="mb-3">
                                                <Label>Code</Label>
                                                <div className="d-flex">
                                                    <Input
                                                        type="text"
                                                        placeholder="Click Generate to create code"
                                                        value={code}
                                                        readOnly
                                                        invalid={isSubmitted && !!errors.code}
                                                    />
                                                    <Button
                                                        color="info"
                                                        type="button"
                                                        className="ms-2"
                                                        onClick={generateCode}
                                                    >
                                                        Generate
                                                    </Button>
                                                </div>
                                                {isSubmitted && errors.code && (
                                                    <FormFeedback className="d-block">{errors.code}</FormFeedback>
                                                )}
                                            </div>
                                            {/* Value */}
                                            <div className="mb-3">
                                                <Label>Value</Label>
                                                <Input
                                                    type="number"
                                                    placeholder="Enter value"
                                                    value={value}
                                                    onChange={e => setValue(e.target.value)}
                                                    invalid={isSubmitted && !!errors.value}
                                                />
                                                {isSubmitted && errors.value && (
                                                    <FormFeedback>{errors.value}</FormFeedback>
                                                )}
                                            </div>

                                            {/* Show Customer Select only if Individual */}
                                            {scope === "individual" && (
                                                <div className="mb-3">
                                                    <Label>Select Customer</Label>
                                                    <AsyncSelect
                                                        cacheOptions
                                                        defaultOptions
                                                        loadOptions={loadCustomerOptions}
                                                        value={selectedCustomer}
                                                        onChange={setSelectedCustomer}
                                                        placeholder="Search by name, email or phone..."
                                                        isClearable
                                                        noOptionsMessage={({ inputValue }) =>
                                                            inputValue ? "No customers found" : "Type to search customers"
                                                        }
                                                        loadingMessage={() => "Searching..."}
                                                    />
                                                    {isSubmitted && errors.customer && (
                                                        <div className="text-danger mt-1">{errors.customer}</div>
                                                    )}
                                                </div>
                                            )}
                                        </Col>

                                        <Col md="6">
                                            {/* Scope */}
                                            <div className="mb-3">
                                                <Label>PromoCode Scope</Label>
                                                <Select
                                                    value={scope ? { value: scope, label: scope.charAt(0).toUpperCase() + scope.slice(1) } : null}
                                                    onChange={(selected) => setScope(selected?.value || "")}
                                                    options={[
                                                        { value: "individual", label: "Individual" },
                                                        { value: "all", label: "All" }
                                                    ]}
                                                    placeholder="-- Select Scope --"
                                                    isClearable
                                                />
                                                {isSubmitted && errors.scope && (
                                                    <div className="text-danger mt-1">{errors.scope}</div>
                                                )}
                                            </div>

                                            {/* Type */}
                                            <div className="mb-3">
                                                <Label>Type</Label>
                                                <Select
                                                    value={type ? { value: type, label: type.charAt(0).toUpperCase() + type.slice(1) } : null}
                                                    onChange={(selected) => setType(selected?.value || "")}
                                                    options={[
                                                        { value: "percent", label: "Percent" },
                                                        { value: "amount", label: "Amount" }
                                                    ]}
                                                    placeholder="-- Select Promo Code Type --"
                                                    isClearable
                                                />
                                                {isSubmitted && errors.type && (
                                                    <div className="text-danger mt-1">{errors.type}</div>
                                                )}
                                            </div>

                                            {/* Service Type */}
                                            <div className="mb-3">
                                                <Label>Type de Service</Label>
                                                <Select
                                                    value={serviceType ? { 
                                                        value: serviceType, 
                                                        label: serviceType.toLowerCase() === 'industrielle' ? 'industrial' : serviceType.charAt(0).toUpperCase() + serviceType.slice(1)
                                                    } : null}
                                                    onChange={(selected) => setServiceType(selected?.value || "")}
                                                    options={[
                                                        { value: "industrielle", label: "industrial" },
                                                        { value: "ridehailing", label: "Ridehailing" }
                                                    ]}
                                                    placeholder="-- Sélectionner le type --"
                                                    isClearable
                                                />
                                                {isSubmitted && errors.serviceType && (
                                                    <div className="text-danger mt-1">{errors.serviceType}</div>
                                                )}
                                            </div>

                                            {/* Category */}
                                            <div className="mb-3">
                                                <Label>PromoCode Category</Label>
                                                <Select
                                                    value={category ? { value: category, label: category.charAt(0).toUpperCase() + category.slice(1) } : null}
                                                    onChange={(selected) => setCategory(selected?.value || "")}
                                                    options={[
                                                        { value: "trip", label: "PromoCodes for Trips" },
                                                        { value: "dates", label: "PromoCodes for Dates" }
                                                    ]}
                                                    placeholder="-- Select Promo Code Category --"
                                                    isClearable
                                                />
                                                {isSubmitted && errors.category && (
                                                    <div className="text-danger mt-1">{errors.category}</div>
                                                )}
                                            </div>

                                            {(category === "dates" || category === "trip") && (
                                                <Row>
                                                    <Col md="6">
                                                        <div className="mb-3">
                                                            <Label>Start Date</Label>
                                                            <InputGroup>
                                                                <Flatpickr
                                                                    className="form-control d-block"
                                                                    placeholder="dd M, yyyy"
                                                                    value={startDate}
                                                                    onChange={(date) => setStartDate(date[0])}
                                                                    options={{
                                                                        altInput: true,
                                                                        altFormat: "F j, Y",
                                                                        dateFormat: "Y-m-d",
                                                                        defaultDate: new Date(),
                                                                        minDate: "today"
                                                                    }}
                                                                />
                                                            </InputGroup>
                                                            {isSubmitted && errors.startDate && (
                                                                <FormFeedback>{errors.startDate}</FormFeedback>
                                                            )}
                                                        </div>
                                                    </Col>
                                                    <Col md="6">
                                                        <div className="mb-3">
                                                            <Label>End Date</Label>
                                                            <InputGroup>
                                                                <Flatpickr
                                                                    className="form-control d-block"
                                                                    placeholder="dd M, yyyy"
                                                                    value={endDate}
                                                                    onChange={(date) => setEndDate(date[0])}
                                                                    options={{
                                                                        altInput: true,
                                                                        altFormat: "F j, Y",
                                                                        dateFormat: "Y-m-d",
                                                                        defaultDate: new Date(),
                                                                        minDate: "today"
                                                                    }}
                                                                />
                                                            </InputGroup>
                                                            {isSubmitted && errors.endDate && (
                                                                <FormFeedback>{errors.endDate}</FormFeedback>
                                                            )}
                                                        </div>
                                                    </Col>
                                                </Row>
                                            )}

                                            {/* Extra inputs based on category */}
                                            {category === "trip" && (
                                                <div className="mb-3">
                                                    <Label>Number of Trips</Label>
                                                    <Input
                                                        type="number"
                                                        placeholder="Enter number of trips"
                                                        value={trips}
                                                        onChange={e => setTrips(e.target.value)}
                                                        invalid={isSubmitted && !!errors.trips}
                                                    />
                                                    {isSubmitted && errors.trips && (
                                                        <FormFeedback>{errors.trips}</FormFeedback>
                                                    )}
                                                </div>
                                            )}
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col className="text-end">
                                            <Button type="submit" color="primary" disabled={loading}>
                                                {loading ? (
                                                    <>
                                                        <span
                                                            className="spinner-border spinner-border-sm me-2"
                                                            role="status"
                                                            aria-hidden="true"
                                                        ></span>
                                                        Saving...
                                                    </>
                                                ) : (
                                                    "Submit"
                                                )}
                                            </Button>
                                        </Col>
                                    </Row>
                                </Form>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default PromoCodesCreate;
